import { List } from '@mui/material'
import { Fragment } from 'react'
import { Divider } from 'utoniq-ui'

import {
  Artist,
  Maybe,
  OrganizationNotificationConnectionFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ScrollFetcher } from '@/components/organisms/functionals'
import {
  NotificationBox,
  NotificationBoxSkeleton,
} from '@/components/organisms/notification'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { individualDisplayResponsive } from '@/core/individual'

export * from './fragments'

export type State = {
  loading: boolean
  refetching: boolean
  hasNextPage: boolean
  notifications: Maybe<OrganizationNotificationConnectionFragment>
  artist: Maybe<Pick<Artist, 'displayName' | 'avatar'>>
}

type NotificationListCellProps = Pick<
  State,
  'loading' | 'hasNextPage' | 'notifications' | 'artist'
> & {
  onFetchMore: Action['onFetchMore']
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

export const ArtistOfficialInformationTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('artistOfficialInformation')
  const { routerNames, generatePath, routerPages, router } =
    useRouter<{ artistId: string }>()
  const displayResponsive = individualDisplayResponsive()
  return (
    <BaseLayout
      header={{
        back: true,
        title: t('title', { displayName: state.artist?.displayName }),
        defaultBackPage: generatePath(routerPages.ArtistTop, {
          artistId:
            (config.applicationType === 'standalone'
              ? config.artistId
              : router.query.artistId) ?? '#',
        }),
        ...(displayResponsive ? { responsive: true, maxWidth: 1120 } : {}),
      }}
      maxWidth={displayResponsive ? 1120 : undefined}
    >
      <Head title={routerNames.OfficialInformation} />
      <NotificationListCell
        loading={state.loading || state.refetching}
        notifications={state.notifications}
        hasNextPage={state.hasNextPage}
        artist={state.artist}
        onFetchMore={onFetchMore}
      />
    </BaseLayout>
  )
}
const NotificationListCell: Cell<NotificationListCellProps> = ({
  loading,
  hasNextPage,
  notifications,
  artist,
  onFetchMore,
}) => {
  const { t } = useTranslation('officialInformation')
  return (
    <CellFrame>
      <ScrollFetcher
        nodes={notifications?.edges.map(({ node }, index) => (
          <Fragment key={node.id}>
            <NotificationBox notification={node} artist={artist} />
            {notifications?.edges.length - 1 > index && (
              <Divider sx={{ mx: 5 }} />
            )}
          </Fragment>
        ))}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        componentProps={{ spacing: 0 }}
        component={List}
        emptyMessage={t('notExist')}
        loadingComponent={
          <List>
            {[1, 2, 3, 4, 5].map(index => (
              <NotificationBoxSkeleton key={index} />
            ))}
          </List>
        }
      />
    </CellFrame>
  )
}
